import React, {
  useContext,
  useState,
  useEffect,
  createContext,
  useCallback,
} from "react";
import {
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, db } from "../services/firebase.js";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { notifySlack } from "./notificationService";
import { normalizeRole, normalizeStatus } from "./authz";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

function computeEffectiveRole(data) {
  if (!data) return null;

  const requestedRole = data.requestedRole === "service" ? "service" : "client";

  const roleRaw =
    data.role === "user"
      ? requestedRole
      : data.role === "service_user"
        ? "service"
        : data.role === "client_user"
          ? "client"
          : data.role;

  const adminRoleRaw =
    data.adminRole === "firmware_admin"
      ? "service_admin"
      : data.adminRole === "admin" || data.adminRole === "superadmin"
        ? "super_admin"
        : data.adminRole;

  const effective = adminRoleRaw || roleRaw || null;

  return normalizeRole(effective);
}

function normalizeUserDoc(data) {
  if (!data) {
    return {
      userStatus: null,
      userRole: null,
      rawRole: null,
      adminRole: null,
    };
  }

  const requestedRole = data.requestedRole === "service" ? "service" : "client";

  const rawRole =
    data.role === "user"
      ? requestedRole
      : data.role === "service_user"
        ? "service"
        : data.role === "client_user"
          ? "client"
          : data.role || null;

  const adminRole =
    data.adminRole === "firmware_admin"
      ? "service_admin"
      : data.adminRole === "admin" || data.adminRole === "superadmin"
        ? "super_admin"
        : data.adminRole || null;

  const userStatus = normalizeStatus(data.status || null);
  const userRole = computeEffectiveRole(data);

  return {
    userStatus,
    userRole,
    rawRole,
    adminRole,
  };
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [rawRole, setRawRole] = useState(null);
  const [adminRole, setAdminRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authTransitioning, setAuthTransitioning] = useState(true);

  const clearUserData = useCallback(() => {
    setUserStatus(null);
    setUserRole(null);
    setRawRole(null);
    setAdminRole(null);
  }, []);

  const applyUserData = useCallback((norm) => {
    setUserStatus(norm.userStatus);
    setUserRole(norm.userRole);
    setRawRole(norm.rawRole);
    setAdminRole(norm.adminRole);
  }, []);

  const loadUserData = useCallback(
    async (user) => {
      if (!user) {
        clearUserData();
        return null;
      }

      const docSnap = await getDoc(doc(db, "users", user.uid));

      if (!docSnap.exists()) {
        clearUserData();
        return null;
      }

      const data = docSnap.data();
      const norm = normalizeUserDoc(data);

      applyUserData(norm);
      return norm;
    },
    [applyUserData, clearUserData],
  );

  const refreshUserData = async () => {
    if (!auth.currentUser) return null;

    setAuthTransitioning(true);

    try {
      return await loadUserData(auth.currentUser);
    } catch (error) {
      console.error("Error refreshing user data:", error);
      clearUserData();
      return null;
    } finally {
      setAuthTransitioning(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthTransitioning(true);

      try {
        if (!user) {
          setCurrentUser(null);
          clearUserData();
          return;
        }

        setCurrentUser(user);
        clearUserData();

        const norm = await loadUserData(user);

        if (!norm) {
          return;
        }
      } catch (e) {
        console.error("Error during auth state change handling:", e);
        setCurrentUser(null);
        clearUserData();
      } finally {
        setLoading(false);
        setAuthTransitioning(false);
      }
    });

    return unsubscribe;
  }, [loadUserData, clearUserData]);

  const register = async (
    email,
    password,
    name,
    surname,
    phone,
    company,
    contactPerson,
    requestedRole = "client",
  ) => {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );

    const uid = userCredential.user.uid;
    const normalizedRequestedRole =
      requestedRole === "service" ? "service" : "client";

    await setDoc(doc(db, "users", uid), {
      name,
      surname,
      email,
      phone,
      company,
      contactPerson: contactPerson || "",
      role: "user",
      status: "pending",
      requestedRole: normalizedRequestedRole,
      createdAt: serverTimestamp(),
    });

    try {
      await notifySlack({
        uid,
        email,
        name: `${name} ${surname}`.trim(),
        surname,
        fullName: `${name} ${surname}`.trim(),
        phone,
        company,
        contactPerson: contactPerson || "",
        requestedRole: normalizedRequestedRole,
        status: "pending",
      });
    } catch (e) {
      console.error("Slack notify exception (ignored):", e);
    }
  };

  const login = async (email, password) => {
    setAuthTransitioning(true);
    clearUserData();

    await setPersistence(auth, browserSessionPersistence);

    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );

    const uid = userCredential.user.uid;
    const userSnap = await getDoc(doc(db, "users", uid));

    if (!userSnap.exists()) {
      await signOut(auth);
      throw new Error("User account not found.");
    }

    const data = userSnap.data();
    const norm = normalizeUserDoc(data);

    if (norm.userStatus === "pending") {
      await signOut(auth);
      throw new Error("Your account is pending admin approval.");
    }

    if (norm.userStatus === "denied") {
      await signOut(auth);
      throw new Error("Your account was denied by an admin.");
    }

    if (!norm.userRole) {
      await signOut(auth);
      throw new Error("User role is not assigned.");
    }

    return {
      userCredential,
      userStatus: norm.userStatus,
      userRole: norm.userRole,
    };
  };

  const logout = async () => {
    setAuthTransitioning(true);
    clearUserData();
    setCurrentUser(null);

    try {
      await signOut(auth);
    } catch (error) {
      setAuthTransitioning(false);
      throw error;
    }
  };

  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  const value = {
    currentUser,
    userStatus,
    userRole,
    rawRole,
    adminRole,
    loading,
    authTransitioning,
    login,
    register,
    logout,
    resetPassword,
    refreshUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
